import React, { useState, useEffect } from 'react';
import dayjs from 'dayjs';

// material-ui
import { Chip, colors, FormControl, InputLabel, MenuItem, Paper, Select } from '@mui/material';
import { CssBaseline, Button, Divider, Grid, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography, Link, Box, IconButton, Popper } from '@mui/material';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';

//components
import AlertInfo from '../../dataObjects/AlertInfo';
import AlertInfoDialog from '../../components/elements/AlertInfoDialog';
import TopBar from '../../components/nav/TopBar';
import AodSchedule from './dataObjects/AodSchedule';
import { AttorneyListEnums } from './dataObjects/AttorneyList';
import Attorney from './dataObjects/Attorney';

// 5 days in a week, 6 rows to accommodate all possible month layouts (42 days)
export type CalendarWeek = [
    AodSchedule | null,
    AodSchedule | null,
    AodSchedule | null,
    AodSchedule | null,
    AodSchedule | null
];

export type WeekdayCalendar = CalendarWeek[];

const LegalStaff = [
    {
        "value": "290698",
        "text": "Sarah Hartry",
        "ytd": 15,
        "color": colors.green[200],
    },
    /*
    {
        "value": "308503",
        "text": "Maura Cronin",
        "ytd": 11,
        "color": colors.blue[200],
    },
    */
    {
        "value": "503185",
        "text": "Evert Fowle",
        "ytd": 14,
        "color": colors.yellow[200],
    },
    {
        "value": "580873",
        "text": "Daniel Barros",
        "ytd": 17,
        "color": colors.pink[200],
    },
    {
        "value": "606941",
        "text": "Hannah Campbell",
        "ytd": 0,
        "color": colors.purple[200],
    }
]

const AttorneyOfTheDayApp = (): JSX.Element => {

    //set up browser title
    const pageTitle = 'Extranet - Attorney Of the Day';
    document.title = pageTitle;

    const applicationName = 'Attorney Of the Day';

    //set up state for the app
    const [activeMonth, setActiveMonth] = useState(new Date().getMonth() + 1);
    const [activeYear, setActiveYear] = useState(new Date().getFullYear());
    const [currentDate, setCurrentDate] = useState(new Date());
    const [calendarWeeks, setCalendarWeeks] = useState<CalendarWeek[]>([]);
    const [monthlyAssignments, setMonthlyAssignments] = useState<AodSchedule[]>();
    const [availableAttorneys, setAvailableAttorneys] = useState<Attorney[]>();
    const [isUserLegalStaff, setIsUserLegalStaff] = useState<boolean>(false);

    //payload
    useEffect(() => {
        checkIfUserLegalStaff();
        getMonthlyAssignments();
    }, [activeMonth]);


    /*Error Handling*/
    const [isAlertInfoDialogOpen, setAlertInfoDialog] = useState(false);
    const [alertInfo, setAlertInfo] = useState<AlertInfo>();
    const closeAlertInfoDialog = () => {
        setAlertInfoDialog(false);
    }

    const updateMonthForward = () => {
        let newDate = dayjs(currentDate).add(1, 'month').toDate();
        setCurrentDate(newDate);
        setActiveMonth(newDate.getMonth() + 1);
        setActiveYear(newDate.getFullYear());
    }

    const updateMonthBackward = () => {
        let newDate = dayjs(currentDate).subtract(1, 'month').toDate();
        setCurrentDate(newDate);
        setActiveMonth(newDate.getMonth() + 1);
        setActiveYear(newDate.getFullYear());
    }

    const generateWeekdayCalendar = (year: Number, month: Number) => {
        const startOfMonth = dayjs(`${year}-${month}-01`);
        const endOfMonth = startOfMonth.endOf("month");

        const weeks: WeekdayCalendar = [];
        let currentWeek: CalendarWeek = [null, null, null, null, null];

        let currentDay = startOfMonth.startOf("week").add(1, "day");

        while (currentDay.isBefore(endOfMonth) || currentDay.isSame(endOfMonth, "day")) {
            const dayOfWeek = currentDay.day();

            if (dayOfWeek >= 1 && dayOfWeek <= 5) {
                const columnIndex = dayOfWeek - 1;

                if (currentDay.month() === startOfMonth.month()) {
                    currentWeek[columnIndex] = {
                        date: currentDay.format("MM/DD/YYYY"),
                        day: currentDay.date(),
                        attorneyId: 0,
                        attorneyName: "",
                        ytdCountAtTime: 1 * (currentDay.month() + 1),
                        isHoliday: false,
                        isFillIn: false,
                        holidayName:""
                    };
                }

                if (dayOfWeek === 5) {
                    weeks.push(currentWeek);
                    currentWeek = [null, null, null, null, null];
                }
            }

            currentDay = currentDay.add(1, "day");
        }

        if (currentWeek.some(day => day !== null)) {
            weeks.push(currentWeek);
        }

        setCalendarWeeks(weeks);
    }

    const getMonthlyAssignments = async () => {
        await fetch(`/AttorneyOfTheDay/GetMonthlyAssignments?year=${activeYear}&month=${activeMonth}`)
            .then(result => result.json())
            .then(result => {
                setMonthlyAssignments(result);
                generateWeekdayCalendar(activeYear, activeMonth);
            }
        )
    }

    const checkIfUserLegalStaff = async () => {
        await fetch(`/AttorneyOfTheDay/IsUserALegalStaffMember`)
            .then(result => result.json())
            .then(result => {
                setIsUserLegalStaff(result);
            }
            )
    }

    const createMonthlyAssignments = () => {
        fetch(`/AttorneyOfTheDay/setMonthlyAssignments?year=${activeYear}&month=${activeMonth}`)
            .then(result => result.json())
            .then(result => {
                getMonthlyAssignments();
            })
    }


    const getAvailableAttorneys = async (targetDate: string) => {
        await fetch(`/AttorneyOfTheDay/GetAvailableAttorneys?date=${targetDate}`)
            .then(result => result.json())
            .then(result => {
                setAvailableAttorneys(result);
            }
        )
    }

    const renderDay = (day: AodSchedule | null, index: number) => {

        if (day) {
            let isToday = dayjs().format('M/D/YYYY') === dayjs(day.date).format('M/D/YYYY');
            let targetDate = dayjs(day.date).format('M/D/YYYY');
            let dayAssignment = monthlyAssignments?.find(assignment => assignment.date === targetDate)
            let staffName = dayAssignment?.attorneyName;
            let staffId = dayAssignment ? dayAssignment.attorneyId : 0;
            let staffColor = getStaffColor(staffId);
            let ytdCount = dayAssignment?.ytdCountAtTime;

            let holidayColor = colors.grey[200];
            let holidayName = "";
            if (dayAssignment?.isHoliday) {
                holidayName = dayAssignment?.holidayName;
            }

            day.attorneyId = staffId;

            let fillingIn = dayAssignment?.isFillIn ? <sup>*</sup> : null; 

            return (
                <TableCell
                    key={dayjs(day.date).format('M/D/YYYY')}
                    align='center'
                    sx={{
                        border: 0.25,
                        borderWidth: isToday?1:0.5,
                        borderColor: isToday ? 'blue' : 'grey.300',
                        position: 'relative',
                        padding: '8px',
                        verticalAlign: 'top',
                        '&.MuiTableCell-root': {
                            boxSizing: 'border-box',
                            minHeight:'220px',
                            minWidth: '100px' // Minimum width to prevent cramping
                        }
                    }}
                >
                    <Stack spacing={0.5}>
                        <Typography variant='subtitle1' fontWeight={550}>{day.day}</Typography>
                        <Typography
                            className={isUserLegalStaff ? "is-clickable" : ""}
                            onClick={isUserLegalStaff ? (event) => openUpdateSelectedAttorneyDialog(event, day) : undefined}
                            component={Box}
                            variant='body1'
                            fontWeight={550}
                            paddingLeft={1}
                            sx={{ bgcolor: !dayAssignment?.isHoliday ? staffColor : holidayColor }}
                        >
                            {!dayAssignment?.isHoliday ?
                                <>{staffName} {fillingIn}</> : holidayName}
                        </Typography>
                        <Typography variant='body2'>
                            {!dayAssignment?.isHoliday && 
                                <i>YTD:{ytdCount}</i>    
                            }
                        </Typography>
                    </Stack>
                    <Popper
                        open={isUpdateSelectedAttorneyDialogOpen}
                        anchorEl={anchorEl}
                        placement={"top"}
                    >
                        <Paper sx={{ p: 2 }}>
                            <Grid item xs={12}>
                                <Stack spacing={2} alignItems="center" display="flex" justifyContent="flex-start">
                                    <Typography variant="subtitle1" fontWeight={550}>
                                        Reassign {selectedAodSchedule?.date} Attorney of the Day:
                                    </Typography>
                                    <FormControl size="small">
                                        <InputLabel>Staff</InputLabel>
                                        <Select
                                            value={selectedAttorneyEmployeeId}
                                            onChange={updateSelectedStaffId}
                                            label="Staff"
                                            sx={{ width: 300 }}
                                        >

                                            <MenuItem value={0}>Please select</MenuItem>
                                            {
                                                availableAttorneys && availableAttorneys.map((staff: Attorney) => (
                                                    <MenuItem key={staff.id} value={staff.id}>{staff.name} (ytd:{staff.ytdDaysWorked})</MenuItem>
                                                ))
                                            }
                                        </Select>
                                    </FormControl>
                                    <Stack direction="row" spacing={1}>
                                        <Button size="small" color="warning" onClick={() => setAnchorEl(null)}>Cancel</Button>
                                        <Button size="small" onClick={reassignAttorney} >Update</Button>
                                    </Stack>
                                </Stack>
                            </Grid>
                        </Paper>
                    </Popper>
                </TableCell>
            );
        }

        return (<TableCell key={index} ></TableCell>)
    };

    const getStaffColor = (staffId: number) => {
        switch (staffId) {
            case AttorneyListEnums.SH:
                return (colors.green[200]);
            case AttorneyListEnums.MC:
                return (colors.blue[200]);
            case AttorneyListEnums.EF:
                return (colors.yellow[200]);
            case AttorneyListEnums.DB:
                return (colors.pink[200]);
            case AttorneyListEnums.HC:
                return (colors.purple[200]);
            default:
                return (colors.grey[200]);
        }
    }

    /*Update the attorney of the day */

    const [selectedAttorneyEmployeeId, setSelectedAttorneyEmployeeId] = useState(0);
    const [selectedAodSchedule, setSelectedAodSchedule] = useState<AodSchedule>();
    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
    const isUpdateSelectedAttorneyDialogOpen = Boolean(anchorEl);

    const openUpdateSelectedAttorneyDialog = (event: React.MouseEvent<HTMLElement>, day: AodSchedule) => {
        setSelectedAodSchedule(day);
        setSelectedAttorneyEmployeeId(day.attorneyId);
        setAnchorEl(anchorEl ? null : event.currentTarget);
        getAvailableAttorneys(day.date);
    };
    const updateSelectedStaffId = (event: any) => {
        setSelectedAttorneyEmployeeId(event.target.value);
    };

    const reassignAttorney = () => {

        if (selectedAodSchedule && selectedAttorneyEmployeeId > 0) {
            fetch(`/AttorneyOfTheDay/ReassignDay?date=${selectedAodSchedule.date}&attorneyId=${selectedAttorneyEmployeeId}`)
                .then(result => result.json())
                .then(result => {
                    if (result.isError) {
                        let info = {
                            type: 'error',
                            title: 'Error: Reassign Day',
                            errorMessage: result.errorMessage,
                            secondaryErrorMessage: result.secondaryErrorMessage
                        };
                        setAlertInfo(info);
                        setAlertInfoDialog(true);
                    }
                    else {
                        setAnchorEl(null);
                        getMonthlyAssignments();
                    }
                }
            )
        }
    }


    //pdf export
    const generateMonthlyPdf = () => {
        let url = `/AttorneyOfTheDay/GetMonthlyPdf?year=${activeYear}&month=${activeMonth}`;
        window.open(url, "_blank", "noopener,noreferrer");
    }

    return (
        <div>
            <Grid container sx={{ display: 'flex' }}>
                <CssBaseline />
                <TopBar applicationName={applicationName}></TopBar>
            </Grid>
            <Grid item xs={12} marginBottom={1}>
                <Divider />
            </Grid>
            <Grid container sx={{ display: 'flex', justifyContent: 'center', marginTop: '40px', marginBottom: '25px' }}>
                <Grid item xs={8} sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Stack direction="row" spacing={1} >
                        {
                            isUserLegalStaff &&
                            <Button variant="outlined" color="success" startIcon={<CalendarTodayIcon />} onClick={createMonthlyAssignments} >
                                Create Schedule
                            </Button>
                        }
                        <Button variant="outlined" startIcon={<PictureAsPdfIcon />} onClick={generateMonthlyPdf} >
                            PDF VIEW
                        </Button>
                    </Stack>
                    <Stack direction="row" spacing={1}>
                        <Typography variant="subtitle1">Color Legend: </Typography>
                        { 
                            LegalStaff && LegalStaff.map((staff: any) => (
                                <Chip
                                    label={staff.text}
                                    key={staff.value}
                                    sx={{
                                        backgroundColor: staff.color
                                    }}
                                />
                            ))
                        }
                    </Stack>
                </Grid>
            </Grid>
            <Grid container sx={{ display: 'flex', justifyContent: 'center', marginBottom: '50px' }} >
                <Grid item xs={8} >
                    <TableContainer>
                        <Table sx={{
                            width: '100%', tableLayout: 'fixed', borderCollapse: 'separate', borderSpacing: 0,
                            '& .MuiTableCell-head': {
                                padding: '16px',
                                fontSize: 'inherit'
                            }
                        }}>
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ backgroundColor: '#00D1B2', color: 'white' }} colSpan={5} align={'center'} >
                                        <Grid container>
                                            <Grid item xs={1} >
                                                <Button aria-label='Go back a month' title='Go back a month' variant='outlined' sx={{ backgroundColor: 'white', color: 'black' }} onClick={() => updateMonthBackward()}><NavigateBeforeIcon /></Button>
                                            </Grid>
                                            <Grid item xs={10} display={'flex'} justifyContent={'center'} alignItems={'center'}>
                                                <Typography variant="h5">
                                                    {dayjs(`${activeYear}-${activeMonth}`, 'YYYY-M').format('MMMM')} {activeYear}
                                                </Typography>
                                            </Grid>

                                            <Grid item xs={1}><Button aria-label='Go forward a month' title='Go forward a month' variant='outlined' sx={{ backgroundColor: 'white', color: 'black' }} onClick={() => updateMonthForward()}><NavigateNextIcon /></Button></Grid>
                                        </Grid>

                                    </TableCell>
                                </TableRow>
                                <TableRow sx={{ backgroundColor: '#f0f0f0' }}>
                                    <TableCell align='center'>{'Monday'}</TableCell>
                                    <TableCell align='center'>{'Tuesday'}</TableCell>
                                    <TableCell align='center'>{'Wednesday'}</TableCell>
                                    <TableCell align='center'>{'Thursday'}</TableCell>
                                    <TableCell align='center'>{'Friday'}</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {calendarWeeks && calendarWeeks.map((week, i) => (
                                    <TableRow key={i}>
                                        {week.map((day, j) => (
                                             renderDay(day, j)
                                        ))}
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Grid>
            </Grid>
            {
                alertInfo &&
                <AlertInfoDialog isAlertInfoDialogOpen={isAlertInfoDialogOpen} alertInfo={alertInfo} closeAlertInfoDialog={closeAlertInfoDialog}></AlertInfoDialog>
            }
        </div>
    );
};

export default AttorneyOfTheDayApp;